import React from 'react';
import { render, act, fireEvent } from '@testing-library/react';
import LiveStreamOverlay from './live_stream_overlay';

// Helper: find the iframe inside the overlay
function getIframe(container) {
    return container.querySelector('iframe');
}

function getOverlayRoot(container) {
    return container.querySelector('[data-live-stream-override="true"]');
}

describe('LiveStreamOverlay (#254)', () => {
    it('renders an iframe with the YouTube embed URL when videoId is provided', () => {
        const { container } = render(
            <LiveStreamOverlay
                videoId="dQw4w9WgXcQ"
                sourceId={7}
                channelId="UCabcdefghijklmnopqrstuv"
                provider="youtube_live"
                fadeOut={false}
                onFadeComplete={jest.fn()}
            />
        );
        const iframe = getIframe(container);
        expect(iframe).not.toBeNull();
        expect(iframe.src).toContain('youtube.com/embed/dQw4w9WgXcQ');
    });

    it('iframe src includes autoplay=1 and mute=1 (kiosk autoplay)', () => {
        const { container } = render(
            <LiveStreamOverlay
                videoId="abc123"
                sourceId={1}
                channelId="UCx"
                provider="youtube_live"
                fadeOut={false}
                onFadeComplete={jest.fn()}
            />
        );
        const iframe = getIframe(container);
        expect(iframe.src).toMatch(/autoplay=1/);
        expect(iframe.src).toMatch(/mute=1/);
        // playsinline + rel=0 for kiosk-friendly behavior
        expect(iframe.src).toMatch(/playsinline=1/);
        expect(iframe.src).toMatch(/rel=0/);
    });

    it('does NOT render an iframe and shows fallback message when videoId is null', () => {
        const { container, getByText } = render(
            <LiveStreamOverlay
                videoId={null}
                sourceId={7}
                channelId="UCabc"
                provider="youtube_live"
                fadeOut={false}
                onFadeComplete={jest.fn()}
            />
        );
        expect(getIframe(container)).toBeNull();
        expect(getByText(/Live stream temporarily unavailable/i)).toBeTruthy();
    });

    it('does NOT render an iframe and shows fallback message when videoId is empty string', () => {
        const { container, getByText } = render(
            <LiveStreamOverlay
                videoId=""
                sourceId={7}
                channelId="UCabc"
                provider="youtube_live"
                fadeOut={false}
                onFadeComplete={jest.fn()}
            />
        );
        expect(getIframe(container)).toBeNull();
        expect(getByText(/Live stream temporarily unavailable/i)).toBeTruthy();
    });

    it('switches to fallback message when the iframe fires onError', () => {
        const { container, getByText, queryByText } = render(
            <LiveStreamOverlay
                videoId="abc123"
                sourceId={7}
                channelId="UCabc"
                provider="youtube_live"
                fadeOut={false}
                onFadeComplete={jest.fn()}
            />
        );
        const iframe = getIframe(container);
        expect(iframe).not.toBeNull();
        expect(queryByText(/Live stream temporarily unavailable/i)).toBeNull();

        act(() => {
            fireEvent.error(iframe);
        });

        expect(getByText(/Live stream temporarily unavailable/i)).toBeTruthy();
        // iframe is replaced with fallback
        expect(getIframe(container)).toBeNull();
    });

    it('round-trips data-* attributes from props to the DOM', () => {
        const { container } = render(
            <LiveStreamOverlay
                videoId="vid42"
                sourceId={42}
                channelId="UCxyz"
                provider="youtube_live"
                fadeOut={false}
                onFadeComplete={jest.fn()}
            />
        );
        const root = getOverlayRoot(container);
        expect(root).not.toBeNull();
        expect(root.getAttribute('data-source-id')).toBe('42');
        expect(root.getAttribute('data-video-id')).toBe('vid42');
        expect(root.getAttribute('data-channel-id')).toBe('UCxyz');
        expect(root.getAttribute('data-provider')).toBe('youtube_live');
    });

    it('round-trips empty data-* attributes when props are null', () => {
        const { container } = render(
            <LiveStreamOverlay
                videoId={null}
                sourceId={null}
                channelId={null}
                provider={null}
                fadeOut={false}
                onFadeComplete={jest.fn()}
            />
        );
        const root = getOverlayRoot(container);
        expect(root.getAttribute('data-source-id')).toBe('');
        expect(root.getAttribute('data-video-id')).toBe('');
        expect(root.getAttribute('data-channel-id')).toBe('');
        expect(root.getAttribute('data-provider')).toBe('');
    });

    it('sets opacity to 0 when fadeOut=true and calls onFadeComplete on transitionEnd', () => {
        const onFadeComplete = jest.fn();
        const { container, rerender } = render(
            <LiveStreamOverlay
                videoId="vid42"
                sourceId={1}
                channelId="UCabc"
                provider="youtube_live"
                fadeOut={false}
                onFadeComplete={onFadeComplete}
            />
        );
        const root = getOverlayRoot(container);
        expect(root.style.opacity).toBe('1');

        rerender(
            <LiveStreamOverlay
                videoId="vid42"
                sourceId={1}
                channelId="UCabc"
                provider="youtube_live"
                fadeOut={true}
                onFadeComplete={onFadeComplete}
            />
        );
        expect(root.style.opacity).toBe('0');

        // Fire transitionEnd for the opacity property
        act(() => {
            fireEvent.transitionEnd(root, { propertyName: 'opacity' });
        });
        expect(onFadeComplete).toHaveBeenCalledTimes(1);
    });

    it('iframe has autoplay + encrypted-media + fullscreen in the allow attribute', () => {
        const { container } = render(
            <LiveStreamOverlay
                videoId="vid42"
                sourceId={1}
                channelId="UCabc"
                provider="youtube_live"
                fadeOut={false}
                onFadeComplete={jest.fn()}
            />
        );
        const iframe = getIframe(container);
        const allow = iframe.getAttribute('allow') || '';
        expect(allow).toMatch(/autoplay/);
        expect(allow).toMatch(/encrypted-media/);
        expect(allow).toMatch(/fullscreen/);
    });
});
